import React from 'react';
import { Typography, Button, Row, Col, Card, Tag, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  RocketOutlined, 
  TeamOutlined, 
  BulbOutlined,
  AimOutlined,
  SendOutlined,
  RocketFilled
} from '@ant-design/icons';
import SEO from '../../components/SEO';
import './AboutPage.css';

const { Title, Paragraph } = Typography;

const AboutPage: React.FC = () => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();

  const goals = [
    {
      icon: <RocketOutlined />,
      title: t('landing.about.goals.automation.title'),
      description: t('landing.about.goals.automation.description')
    },
    {
      icon: <TeamOutlined />,
      title: t('landing.about.goals.community.title'),
      description: t('landing.about.goals.community.description')
    },
    {
      icon: <BulbOutlined />,
      title: t('landing.about.goals.innovation.title'),
      description: t('landing.about.goals.innovation.description')
    },
    {
      icon: <AimOutlined />,
      title: t('landing.about.goals.quality.title'),
      description: t('landing.about.goals.quality.description')
    }
  ];

  return (
    <>
      <SEO
        title="О нас - QATE | История и миссия платформы управления тестированием"
        description="Узнайте больше о команде QATE и нашей миссии по революционизации процессов QA. Создаем инновационные решения для управления тестированием с поддержкой ИИ."
        keywords="о нас QATE, команда QATE, миссия QA платформы, история тест менеджмента, разработчики QATE, about us, QA team"
        canonicalUrl="/about"
        ogTitle="О нас - QATE | История и миссия платформы"
        ogDescription="Узнайте больше о команде QATE и нашей миссии по революционизации процессов QA. Создаем инновационные решения для управления тестированием."
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "mainEntity": {
            "@type": "Organization",
            "name": "QATE",
            "description": "Компания, разрабатывающая AI-powered платформу управления тестированием",
            "url": "https://qate.ru",
            "foundingDate": "2025",
            "mission": "Революционизация процессов управления качеством ПО через автоматизацию и ИИ"
          }
        }}
      />
      <div className="about-page">
        <div className="about-container">
          <Row justify="center" align="middle" className="hero-section">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <div className="alpha-tag">
                <Tag color="blue">
                  {t('landing.about.alphaTag')}
                </Tag>
              </div>
              <Title level={1} className="hero-title">{t('landing.about.heroTitle')}</Title>
              <Paragraph className="hero-subtitle">
                {t('landing.about.heroSubtitle')}
              </Paragraph>
              <Paragraph className="alpha-description">
                {t('landing.about.alphaDescription')}
              </Paragraph>
            </Col>
          </Row>

          <Row gutter={[48, 48]} className="about-stats">
            {goals.map((goal, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <Card className="stat-card">
                  <div className="stat-icon">{goal.icon}</div>
                  <div className="stat-number">{goal.title}</div>
                  <div className="stat-label">{goal.description}</div>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="about-content">
            <Row gutter={[48, 48]}>
              <Col xs={24} lg={12}>
                <div className="about-section">
                  <Title level={2}>{t('landing.about.mission.title')}</Title>
                  <Paragraph>
                    {t('landing.about.mission.description')}
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <div className="about-section">
                  <Title level={2}>{t('landing.about.values.title')}</Title>
                  <Paragraph>
                    {t('landing.about.values.description')}
                  </Paragraph>
                </div>
              </Col>
            </Row>
          </div>

          <Row justify="center" className="about-cta">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <Title level={2}>{t('landing.about.ctaTitle')}</Title>
              <Paragraph className="cta-subtitle">
                {t('landing.about.ctaSubtitle')}
              </Paragraph>
              <Space size="middle" className="cta-buttons">
                <Button 
                  type="primary" 
                  size="large"
                  onClick={() => navigate('/register')}
                  className="cta-button primary-button"
                  icon={<RocketFilled />}
                >
                  {t('landing.about.startFree')}
                </Button>
                <Button 
                  type="default" 
                  size="large"
                  href="https://t.me/qate_support"
                  target="_blank"
                  className="cta-button secondary-button"
                  icon={<SendOutlined />}
                >
                  {t('landing.about.telegram')}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default AboutPage; 